import { configureStore } from '@reduxjs/toolkit';
import { filesReducer } from './slices/filesSlice';
import { editorReducer } from './slices/editorSlice';
import { uiReducer } from './slices/uiSlice';
import { projectReducer } from './slices/projectSlice';
import { recentPhrasesReducer } from './slices/recentPhrasesSlice';
import privacyReducer from './slices/privacySlice';
import revisionReducer from '../features/revisions/store/revisionSlice';
import searchReducer from '../features/search/store/searchSlice';
import { baseApi } from './api/baseApi';
import { storageApi } from './api/unifiedStorageApi';
import { revisionApi } from '../features/revisions/api/revisionApi';
import { searchApi } from '../features/search/api/searchApi';
import { recentPhrasesMiddleware } from './middleware/recentPhrasesMiddleware';

export const store = configureStore({
  reducer: {
    files: filesReducer,
    editor: editorReducer,
    ui: uiReducer,
    project: projectReducer,
    recentPhrases: recentPhrasesReducer,
    privacy: privacyReducer,
    revisions: revisionReducer,
    search: searchReducer,
    [baseApi.reducerPath]: baseApi.reducer,
    [storageApi.reducerPath]: storageApi.reducer,
    [revisionApi.reducerPath]: revisionApi.reducer,
    [searchApi.reducerPath]: searchApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      // Enhanced serialization checks for better development experience
      serializableCheck: {
        ignoredActionsPaths: ['payload.timestamp'],
        ignoredPaths: [
          baseApi.reducerPath + '.queries',
          storageApi.reducerPath + '.queries',
          revisionApi.reducerPath + '.queries',
          searchApi.reducerPath + '.queries',
        ],
      },
      // Disable immutability check in production for performance
      immutableCheck: import.meta.env.DEV,
    }).concat(
      baseApi.middleware,
      storageApi.middleware,
      revisionApi.middleware,
      searchApi.middleware,
      recentPhrasesMiddleware
    ),

  // Enhanced Redux DevTools configuration
  devTools: import.meta.env.DEV && {
    name: 'Phrasewright Redux Store',
    trace: true,
    traceLimit: 25,
    actionSanitizer: (action: any) => {
      // Sanitize large payloads for better DevTools performance
      if (action.type.includes('executeQuery') && action.payload?.data) {
        return {
          ...action,
          payload: {
            ...action.payload,
            data: '<<TRUNCATED_FOR_DEVTOOLS>>',
          },
        };
      }
      return action;
    },
    stateSanitizer: (state: any) => {
      // Sanitize API cache data for DevTools readability
      let sanitizedState = state;

      if (state[baseApi.reducerPath]?.queries) {
        sanitizedState = {
          ...sanitizedState,
          [baseApi.reducerPath]: {
            ...state[baseApi.reducerPath],
            queries: '<<API_CACHE_TRUNCATED>>',
          },
        };
      }

      if (state[storageApi.reducerPath]?.queries) {
        sanitizedState = {
          ...sanitizedState,
          [storageApi.reducerPath]: {
            ...state[storageApi.reducerPath],
            queries: '<<STORAGE_CACHE_TRUNCATED>>',
          },
        };
      }

      if (state[revisionApi.reducerPath]?.queries) {
        sanitizedState = {
          ...sanitizedState,
          [revisionApi.reducerPath]: {
            ...state[revisionApi.reducerPath],
            queries: '<<REVISION_CACHE_TRUNCATED>>',
          },
        };
      }

      if (state[searchApi.reducerPath]?.queries) {
        sanitizedState = {
          ...sanitizedState,
          [searchApi.reducerPath]: {
            ...state[searchApi.reducerPath],
            queries: '<<SEARCH_CACHE_TRUNCATED>>',
          },
        };
      }

      return sanitizedState;
    },
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Enhanced store utilities for debugging and testing
export const getStoreState = () => store.getState();
export const dispatchAction = store.dispatch;
